import { TabId } from '@/domain/types';

interface BottomNavProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  dimmed?: boolean; // Per spec: opacity 0.4-0.5 during active session
}

// SVG Icons
const HomeIcon = () => (
  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const ProgressIcon = () => (
  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);

const InviteIcon = () => (
  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const icons: Record<TabId, () => JSX.Element> = {
  tap: HomeIcon,
  progress: ProgressIcon,
  invite: InviteIcon,
};

export function BottomNav({ activeTab, onTabChange, dimmed = false }: BottomNavProps) {
  const tabs: { id: TabId; label: string }[] = [
    { id: 'tap', label: 'GAME' },
    { id: 'progress', label: 'PROGRESS' },
    { id: 'invite', label: 'INVITE' },
  ];

  return (
    <nav
      className={`flex h-16 shrink-0 items-center justify-around px-2 transition-all duration-300 ${
        dimmed ? 'opacity-45 pointer-events-none' : 'opacity-100'
      }`}
      style={{
        background: 'rgba(23, 37, 84, 0.4)',
        backdropFilter: dimmed ? 'blur(8px)' : 'blur(16px)',
        WebkitBackdropFilter: dimmed ? 'blur(8px)' : 'blur(16px)',
        borderTop: '1px solid rgba(6, 182, 212, 0.2)',
      }}
    >
      {tabs.map((tab) => {
        const Icon = icons[tab.id];
        const isActive = activeTab === tab.id;
        const isTapActive = tab.id === 'tap' && isActive;

        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            disabled={dimmed}
            className={`flex flex-1 flex-col items-center gap-1 py-2 transition-all duration-200 ${
              dimmed ? '' : 'active:scale-95'
            } ${
              isActive
                ? isTapActive
                  ? 'text-primary-400'
                  : 'text-glow-cyan'
                : 'text-blue-300/50 hover:text-blue-300/70'
            }`}
            style={{
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Icon />
            <span className="text-[10px] font-semibold uppercase tracking-wide">{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
